import './App.css';
import { Flex } from '@adobe/react-spectrum';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useLocation, Outlet, ScrollRestoration } from 'react-router-dom';
import { HeaderBar } from './components/headerBar/HeaderBar';
import { FooterBar } from './components/footerBar/FooterBar';
import { handleUpdatedAppRoute } from './data/redux/slices/appSlice';
import { getAnalytics, logEvent } from 'firebase/analytics';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import useBuyMeACoffee from './hooks/useBuyMeACoffee';
import { parseHash } from './utils/deepLink/useDeepLinkSegment';

export default function AppBody(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const route = useSelector((state) => state.app.route);
    const dispatch = useDispatch();
    const auth = useAuth();
    const userProfileName = useSelector((state) => state.userDetails?.profile?.metadata?.profileName);

    const prevRoute = useRef(route?.path || '/');

    // Fire once per session if the user landed directly on a details page.
    // Lets us distinguish external/shared entry vs. in-app navigation.
    useEffect(() => {
        try {
            const path = window.location.pathname;
            let kind = null;
            if (/^\/details\/deck\//.test(path)) kind = 'DECK';
            else if (/^\/commanders\/id\//.test(path)) kind = 'COMMANDER';
            else if (/^\/user\/id\//.test(path)) kind = 'USER';
            else if (/^\/authors\/id\//.test(path)) kind = 'AUTHOR';
            if (kind) {
                logEvent(getAnalytics(), `entry_via_${kind}`, { entry_page: path });
            }
        } catch (e) {
            // analytics not initialized yet
        }
    }, []);

    // Fire once per session if the user arrived with a deep-link hash.
    // Flattens segments + params into GA4-friendly primitives so new deep-link
    // shapes get captured without code changes here.
    useEffect(() => {
        if (!window.location.hash) return;
        try {
            const { segments, params } = parseHash(window.location.hash);
            const raw = window.location.hash.replace(/^#/, '');
            const clip = (v) => String(v).slice(0, 100);
            const payload = {
                entry_page: window.location.pathname,
                deep_link: clip(raw),
                deep_link_path: clip(segments.join('/')),
                deep_link_param_keys: clip(Object.keys(params).sort().join(',')),
            };
            for (const [k, v] of Object.entries(params)) {
                const key = `dl_${k}`.slice(0, 40);
                payload[key] = clip(v);
            }
            logEvent(getAnalytics(), 'entry_via_deep_link', payload);
        } catch (e) {
            // analytics not initialized yet
        }
    }, []);

    // Initialize Buy Me a Coffee widget
    // useBuyMeACoffee();

    // Manual page_view logging (automatic tracking is disabled in Firebase config).
    // Wait for react-helmet to settle document.title before firing so GA4 records
    // a meaningful page_title instead of a "Loading..." placeholder, but always
    // fire within 500ms so slow/errored title swaps don't drop the pageview.
    useEffect(() => {
        let fired = false;
        const fire = () => {
            if (fired) return;
            fired = true;
            try {
                logEvent(getAnalytics(), 'page_view', {
                    page_title: document.title,
                    page_location: window.location.href,
                    page_path: location.pathname,
                });
            } catch (e) {
                // analytics not initialized yet
            }
        };

        const titleEl = document.querySelector('title');
        const observer =
            titleEl && typeof MutationObserver !== 'undefined'
                ? new MutationObserver(() => {
                      if (!document.title.includes('Loading...')) fire();
                  })
                : null;
        observer?.observe(titleEl, { childList: true });

        const fallback = setTimeout(fire, 500);

        if (!document.title.includes('Loading...')) fire();

        return () => {
            clearTimeout(fallback);
            observer?.disconnect();
        };
    }, [location.pathname]);

    if (
        /\/user\/id\/[0-9a-z]{8}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{12}/.test(window.location.href) &&
        userProfileName &&
        userProfileName !== ''
    ) {
        window.history.replaceState({}, null, `/user/id/${userProfileName}`);
    }

    useEffect(() => {
        if (!window.location.href.includes('?code=')) {
            let currentRoute = route?.path;

            if (prevRoute.current !== currentRoute) {
                const uri = window.location.pathname;
                let adoptingUrlAsRoute = false;

                if (!currentRoute && uri !== prevRoute.current) {
                    currentRoute = uri;
                    adoptingUrlAsRoute = true;
                }

                prevRoute.current = currentRoute || '/';

                navigate(currentRoute + (adoptingUrlAsRoute ? window.location.hash : ''));
                dispatch(handleUpdatedAppRoute({ route: currentRoute, firstRouteHandled: false }));
            }
        }
    }, [route]);

    window.addEventListener('popstate', (event) => {
        if (window.location.href !== route.path && !window.location.href.includes('?code=')) {
            const uri = window.location.pathname;

            if (prevRoute.current !== uri) {
                prevRoute.current = uri;
                dispatch(handleUpdatedAppRoute({ route: uri }));
            }
        }
    });

    return (
        // <ScrollRestoration>
        <div style={{ maxWidth: '100vw', overflowX: 'clip' }}>
            <Flex id="AppContainer" direction="column">
                <HeaderBar />
                <Flex id="ContentContainer" direction="row" justifyContent="center">
                    <Flex
                        id="CenterContentContainer"
                        direction="column"
                        marginBottom="85px"
                        maxWidth={2000}
                        width={'100vw'}
                        // width={2000}
                    >
                        <Outlet />
                    </Flex>
                </Flex>
                <FooterBar />
            </Flex>
        </div>
        // </ScrollRestoration>
    );
}
